import React, { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { TemplateForm } from './TemplateForm'
import { CollaboratorSelector } from './CollaboratorSelector'
import { useCreateTemplate, useCreateAssessment, useListTemplates } from '@/api/bonificacao-hooks'
import { CheckCircle, Loader2 } from 'lucide-react'
import type { BonusCategoria } from './types'

interface SelectedEmployee {
  employeeId: string
  name: string
  department?: string
  role?: string
}

interface CycleSetupSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  year: number
  existingTemplateId?: string
}

export function CycleSetupSheet({ open, onOpenChange, year, existingTemplateId }: CycleSetupSheetProps) {
  const queryClient = useQueryClient()
  const createTemplate = useCreateTemplate()
  const createAssessment = useCreateAssessment()

  const [activeTab, setActiveTab] = useState('template')
  const [templateName, setTemplateName] = useState(`Bonificacao Diretores ${year}`)
  const [categorias, setCategorias] = useState<BonusCategoria[]>([])
  const [selectedEmployees, setSelectedEmployees] = useState<SelectedEmployee[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitProgress, setSubmitProgress] = useState('')
  const [templateReady, setTemplateReady] = useState(false)

  // Edit mode: load existing template
  const { data: templates } = useListTemplates(year, { enabled: open && !!existingTemplateId })

  React.useEffect(() => {
    if (existingTemplateId && templates) {
      const tmpl = templates.find(t => t.templateId === existingTemplateId)
      if (tmpl) {
        setTemplateName(tmpl.name)
        setCategorias(tmpl.categorias)
        setTemplateReady(true)
      }
    }
  }, [existingTemplateId, templates])

  const pesoTotal = categorias.reduce((sum, c) => sum + c.peso, 0)
  const isPesoValid = Math.abs(pesoTotal - 100) < 0.01
  const allIndicadoresNamed = categorias.every(c =>
    c.nome.trim().length > 0 && c.indicadores.every(i => i.nome.trim().length > 0)
  )
  const canAdvanceToCollaborators = templateName.trim().length > 0 && categorias.length > 0 && isPesoValid && allIndicadoresNamed

  const handleAdvanceToCollaborators = useCallback(() => {
    if (canAdvanceToCollaborators) {
      setTemplateReady(true)
      setActiveTab('collaborators')
    }
  }, [canAdvanceToCollaborators])

  const handleSubmit = useCallback(async () => {
    if (selectedEmployees.length === 0 || !canAdvanceToCollaborators) return

    setIsSubmitting(true)
    try {
      // Step 1: Create template (or use existing)
      let templateId = existingTemplateId
      if (!templateId) {
        setSubmitProgress('Criando template...')
        const template = await createTemplate.mutateAsync({
          name: templateName,
          year,
          categorias,
        })
        templateId = template.templateId
      }

      // Step 2: Create assessments for each employee
      for (let i = 0; i < selectedEmployees.length; i++) {
        const emp = selectedEmployees[i]
        setSubmitProgress(`Criando avaliacao ${i + 1}/${selectedEmployees.length}: ${emp.name}...`)
        await createAssessment.mutateAsync({
          employeeId: emp.employeeId,
          templateId: templateId!,
          year,
        })
      }

      setSubmitProgress('Concluido!')

      // Invalidate dashboard to refresh
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })

      // Close after short delay to show success
      setTimeout(() => {
        onOpenChange(false)
        resetState()
      }, 800)
    } catch (error) {
      console.error('Failed to create cycle:', error)
      setSubmitProgress(`Erro: ${error instanceof Error ? error.message : 'Falha ao criar ciclo'}`)
      setIsSubmitting(false)
    }
  }, [selectedEmployees, canAdvanceToCollaborators, existingTemplateId, templateName, year, categorias, createTemplate, createAssessment, queryClient, onOpenChange])

  const resetState = () => {
    setActiveTab('template')
    setTemplateName(`Bonificacao Diretores ${year}`)
    setCategorias([])
    setSelectedEmployees([])
    setIsSubmitting(false)
    setSubmitProgress('')
    setTemplateReady(false)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isSubmitting) onOpenChange(v) }}>
      <DialogContent
        className="bg-brand-dark border-brand-gray/30 text-white sm:max-w-[540px] max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display">
            {existingTemplateId ? 'Editar' : 'Configurar'} Ciclo {year}
          </DialogTitle>
        </DialogHeader>

        {isSubmitting ? (
          <div className="flex flex-col items-center gap-4 py-8">
            {submitProgress.startsWith('Concluido') ? (
              <CheckCircle className="h-12 w-12 text-green-400" />
            ) : (
              <Loader2 className="h-12 w-12 animate-spin text-brand-primary" />
            )}
            <p className="text-sm text-brand-gray">{submitProgress}</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="template">
                1. Template {templateReady && <CheckCircle className="inline ml-1 h-3 w-3 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="collaborators">
                2. Colaboradores
              </TabsTrigger>
            </TabsList>

            <TabsContent value="template">
              <TemplateForm
                name={templateName}
                onNameChange={setTemplateName}
                categorias={categorias}
                onCategoriasChange={setCategorias}
                pesoTotal={pesoTotal}
                isPesoValid={isPesoValid}
              />
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  className="btn-primary px-6 py-2 rounded-monumenta text-sm font-medium disabled:opacity-40"
                  disabled={!canAdvanceToCollaborators}
                  onClick={handleAdvanceToCollaborators}
                >
                  Proximo: Colaboradores &rarr;
                </button>
              </div>
            </TabsContent>

            <TabsContent value="collaborators">
              <CollaboratorSelector
                selectedEmployees={selectedEmployees}
                onSelectedChange={setSelectedEmployees}
                year={year}
              />
              <div className="flex justify-between mt-4">
                <button
                  type="button"
                  className="btn-ghost px-4 py-2 rounded-monumenta text-sm"
                  onClick={() => setActiveTab('template')}
                >
                  &larr; Voltar
                </button>
                <button
                  type="button"
                  className="btn-primary px-6 py-2 rounded-monumenta text-sm font-medium disabled:opacity-40"
                  disabled={selectedEmployees.length === 0}
                  onClick={handleSubmit}
                >
                  Iniciar Ciclo ({selectedEmployees.length} colaborador{selectedEmployees.length !== 1 ? 'es' : ''})
                </button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
